<template>
  <div class="ProjectView" v-if="p && p.uuid">
    <help-message>
      <h3 slot="title">Page de vue de projet</h3>
      <p>
        Cette page est une synthèse des données entrées pour le projet
        <strong>{{ p.name }}</strong
        >. <br />Par la suite, elle contiendra des liens vers les entités
        qu'elle référence : vous pourrez cliquer sur les typologies pour trouver
        les projets similaires, sur les contacts pour trouver les projets sur
        lesquels tel contact est intervenu, les dates, [...].
      </p>
    </help-message>
    <div class="columns">
      <div class="column is-one-quarter">
        <api-picture :picture-id="p.main_image"></api-picture>
      </div>
      <div class="column is-three-quarters">
        <h1 class="title">{{ p.code }} : {{ p.name }}&nbsp;</h1>
        <h2 class="subtitle">
          {{ p.address }}, {{ p.zipcode }} {{ p.city }}, {{ p.country }}<br />{{
            p.lat
          }}°N,{{ p.lng }}°E
        </h2>
        <p>
          <router-link class="button" :to="`/project/pictures/${p.uuid}`"
          ><i class="fa fa-image"></i>&nbsp; images et crédits </router-link
          >&nbsp;<router-link class="button" :to="`/project/edit/${p.uuid}`"
            ><i class="fa fa-edit"></i> éditer </router-link
          >&nbsp;<a
            class="button"
            :href="
              `${$options.defaultApiUrl}print/project/${p.uuid}?token=${
                $store.state.user.token
              }`
            "
            ><i class="fa fa-print"></i>&nbsp;imprimer</a
          >&nbsp;<router-link class="button" :to="`/projects/view/${p.uuid}/contacts`"
            ><i class="fa fa-file"></i>&nbsp;fiche contact</router-link
          >&nbsp;<a class="is-small button has-text-black" @click="basket(p)"
            ><i class="fa fa-folder-open"></i> ajouter au classeur</a
          >
        </p>
      </div>
    </div>
    <violet-bar>En bref</violet-bar>
    <div class="squarebox">
      <div class="columns">
        <div class="column is-one-third">
          <h3 class="subtitle">Surface / coût</h3>
          <ul>
            <li>
              Coût : <span v-if="p.cost">{{ formatNumbers(p.cost) }}€</span>
            </li>
          </ul>
          <ul>
            <li v-for="s in p.surfaces_d" :key="s.surface_id">
              {{ s.text }} :
              <span v-if="s.content">{{ formatNumbers(s.content) }}m²</span>
            </li>
          </ul>
        </div>
        <div class="column is-one-third">
          <h3 class="subtitle">Nature du projet</h3>
          <ul>
            <li>Typologies : {{ mapNames(p.typologies) }}</li>
            <li>Programme : {{ mapNames(p.programs) }}</li>
            <li>Programme Scalène : {{ p.program_scalene }}</li>
          </ul>
        </div>
        <div class="column is-one-third">
          <h3 class="subtitle">Contexte</h3>
          <ul>
            <li>Procédure : {{ mapNames(p.procedures) }}</li>
            <li>Mission: {{ mapNames(p.missions) }}</li>
          </ul>
        </div>
      </div>
      <div class="columns">
        <div class="column is-one-third">
          <h3 class="subtitle">Chronologie</h3>
          <ul>
            <li v-for="(d, index) in p.dates_d" :key="index">
              {{ d.text }} : {{ d.content }}
            </li>
          </ul>
        </div>
        <div class="column is-one-third">
          <div>
            <h3 class="subtitle">Spécificités</h3>
            <ul>
              <li>Récompenses : {{ mapNames(p.awards) }}</li>
              <li>Labels : {{ mapNames(p.labels) }}</li>
              <li>Normes: {{ mapNames(p.normes) }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div>
      <help-message>
        <h3 slot="title">Interlocuteurs</h3>
        <p>
          Rappel : les <strong>Interlocuteurs</strong> sont la jonction d'un
          <strong>rôle</strong>, d'une <strong>entreprise</strong>, d'un
          <strong>contact</strong> et de sa <strong>fonction</strong> le temps
          d'un <strong>projet</strong>.<br />Leur saisie ne modifie pas les
          <strong>contacts</strong> ou <strong>entreprises</strong> concernées.
        </p>
      </help-message>
      <violet-bar>Interlocuteurs</violet-bar>
      <table
        class="table is-bordered is-hoverable is-striped is-fullwidth is-size-7"
      >
        <thead>
          <tr>
            <td>Rôle</td>
            <td>Entreprise</td>
            <td>Contact</td>
            <td>Qualité</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(i, index) in p.interlocuteurs_d" :key="index">
            <td>{{ i.role.name }}</td>
            <td style="display:flex; align-items:flex-start">
              <api-picture
                style="width: 40px;margin-right: 10px;"
                :picture-id="i.partner.logo_id"
              />
              <span>{{ i.partner.name }}</span>
            </td>
            <td>
              {{ i.contact.first_name }} {{ i.contact.last_name }}
              <span v-if="i.contact.fonction"
                >({{ i.contact.fonction.name }})</span
              >
            </td>
            <td>{{ i.statut.name }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <hr />
    <div>
      <violet-bar>Axes du projet</violet-bar>
      <div
        class="squarebox"
        v-for="(axe, index) in p.contents_d.filter(a => a.axe_id)"
        :key="index"
      >
        <h3 class="subtitle">{{ axe.name }}</h3>
        <blockquote style="max-width: 76ch">{{ axe.text }}</blockquote>
        <div style="display: flex; flex-wrap: wrap; margin-left: -1ch;">
          <div
            style="width: 230px; margin: 1ch"
            v-for="(pic, jdex) in axe.pictures"
            :key="jdex"
          >
            <figure v-if="pic.picture" class="squarebox">
              <div
                style="width: 100%; height: 0; padding-bottom: 67%; position:relative;overflow: hidden"
              >
                <bound-api-picture
                  :picture-id="pic.picture.id"
                  :ratio="67 / 100"
                ></bound-api-picture>
              </div>
              <figcaption
                style="font-size: 11px; line-height: 1; margin-top: 1em;"
              >
                {{ pic.text }}
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { types as t } from "../../store/actions/projects";
import { formatNumbers } from "../../util/func";
import ApiPicture from "../../components/Form/ApiPicture";
import HelpMessage from "../../components/Form/HelpMessage";
import { defaultApiUrl } from "../../api";
import VioletBar from "./VioletBar";
import BoundApiPicture from "../Export/Fiche/Index/Image/BoundApiPictureIndex";
import {types as basketActions} from "../../store/actions/basket";
import {ITEM_TYPES as basketItemTypes} from "../../store/mutations/basket";

export default {
  name: "ProjectView",
  components: { BoundApiPicture, VioletBar, HelpMessage, ApiPicture },
  props: {
    uuid: {
      type: String,
      required: true
    }
  },
  defaultApiUrl,
  data() {
    return {
      p: {},
      loading: false
    };
  },
  async mounted() {
    this.p = await this.$store.dispatch(t.GET_PROJECT_BY_UUID_D, {
      uuid: this.uuid
    });
  },
  methods: {
    formatNumbers,
    mapNames(arr) {
      return arr.map(a => a.name).join(", ");
    },
    basket(p) {
      this.$store.dispatch(basketActions.ADD_BASKET_ITEM, {
        item: p,
        type: basketItemTypes.PROJECT
      });
    },
  }
};
</script>

<style lang="scss">
.ProjectEdit {
}

.ProjectContacts {
  padding: 16px;
  border-radius: 3px;
  margin: 1em 0;
}

.ProjectView {
  margin-bottom: 5em;
}
</style>
